// Login.js
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Col, Form, FormGroup, Row } from 'reactstrap';
import Parse from 'parse';
import classNames from 'classnames';
import FontAwesome from 'react-fontawesome';
import FAStyles from 'styles/global/font-awesome.css';
import withStyles from 'isomorphic-style-loader/withStyles';
import queryString from 'query-string'; // Added for reason parsing

import FloatingLabel from 'components/FloatingLabel';
import Link from 'components/Link';
import LoadingButton from 'components/LoadingButton';
import history from 'core/history';
import { ErrorAlert } from 'helpers/alert';
import { loginUser } from 'actions/user';
import handleParseError from 'helpers/handleParseError';

import s from './Login.scss';

class Login extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    loginUser: PropTypes.func.isRequired,
    next: PropTypes.string,
    location: PropTypes.shape({
      // Added for location prop
      search: PropTypes.string,
      pathname: PropTypes.string,
      // hash: PropTypes.string, // If needed
      // state: PropTypes.object, // If needed
    }),
  };

  static defaultProps = {
    next: null,
    location: { search: '', pathname: '/' }, // Default for location
  };

  constructor(props) {
    super(props);
    this.state = { email: '', password: '', isLoading: false };
    this._isMounted = false; // Added

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    const { location } = this.props; // Assuming React Router passes location
    if (location && location.search) {
      const params = queryString.parse(location.search);
      const { reason } = params;
      let alertShown = false;

      if (reason === 'session_init_failed_roles') {
        ErrorAlert(
          'Your session was validated, but critical user role information could not be loaded. Please try logging in again.',
        );
        alertShown = true;
      } else if (reason === 'session_init_failed_api') {
        ErrorAlert(
          'Your session was validated, but server registration failed. Please try logging in again.',
        );
        alertShown = true;
      } else if (reason === 'session_validation_failed') {
        ErrorAlert(
          'Your previous session could not be validated. Please log in again.',
        );
        alertShown = true;
      }

      if (alertShown) {
        // Remove 'reason' from query params
        delete params.reason;
        const newSearch = queryString.stringify(params);
        history.replace({
          pathname: location.pathname,
          search: newSearch,
          // hash: location.hash, // Preserve hash if needed
          // state: location.state, // Preserve state if needed
        });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleChange(event) {
    const { name } = event.target;
    const newState = {};
    newState[name] = event.target.value;
    this.setState(newState);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.state.email === '' || this.state.password === '') {
      ErrorAlert('Please complete the form');
      return;
    }

    this.setState({ isLoading: true });
    const parseLoginStartTime = performance.now();

    // Add timeout specifically for login
    const loginPromise = Parse.User.logIn(
      this.state.email.toLowerCase(),
      this.state.password,
    );

    // Wrap with a more aggressive timeout for login
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error('Login timeout. Please check your connection.')),
        15000,
      ),
    );

    Promise.race([loginPromise, timeoutPromise])
      .then(user => {
        if (__DEV__) {
          console.log(
            `[PERF_LOG] Login took ${performance.now() -
              parseLoginStartTime}ms`,
          );
        }

        if (!this._isMounted) return;

        // Dispatch login action
        return this.props.loginUser(user);
      })
      .then(() => {
        if (!this._isMounted) return;

        // Redirect after successful login
        const redirectTo = this.props.next
          ? decodeURIComponent(this.props.next)
          : '/';

        history.push(redirectTo);
      })
      .catch(error => {
        if (__DEV__) {
          console.error('[Login Error]', error);
        }

        // Check for specific error types
        if (error.message && error.message.includes('timeout')) {
          ErrorAlert(
            'Login timed out. Please check your internet connection and try again.',
          );
        } else if (error.code === Parse.Error.CONNECTION_FAILED) {
          ErrorAlert(
            'Cannot connect to server. Please check your internet connection.',
          );
        } else {
          // Use existing error handler
          handleParseError(err => {
            if (!this._isMounted) return;
            ErrorAlert(err.message);
          })(error);
        }
      })
      .finally(() => {
        if (this._isMounted) {
          this.setState({ isLoading: false });
        }
      });
  }

  /* eslint-disable css-modules/no-undef-class */
  render() {
    return (
      <div className={classNames([s.root, 'jumbotron', 'text-center'])}>
        <div className={s.container}>
          <h1>{this.props.title}</h1>
          <Row className="justify-content-center">
            <Col sm={4}>
              <Form onSubmit={this.handleSubmit} method="post">
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <Col sm={12}>
                        <FloatingLabel
                          placeholder="Email"
                          id="email"
                          name="email"
                          type="email"
                          value={this.state.email}
                          inputAdditionalStyles={{
                            border: 0,
                            borderTopRightRadius: '5px',
                            borderBottomRightRadius: '5px',
                          }}
                          labelAdditionalStyles={{ left: '18%' }}
                          labelFocusedAdditionalStyles={{ color: 'white' }}
                          onChange={this.handleChange}
                          errorMessage="Please enter a valid email."
                          isRequired
                        >
                          <div className="input-group-prepend">
                            <div className="input-group-text">
                              <FontAwesome name="user" className="fa-fw" />
                            </div>
                          </div>
                        </FloatingLabel>
                      </Col>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <Col sm={12}>
                        <FloatingLabel
                          placeholder="Password"
                          type="password"
                          name="password"
                          id="password"
                          value={this.state.password}
                          inputAdditionalStyles={{
                            border: 0,
                            borderTopRightRadius: '5px',
                            borderBottomRightRadius: '5px',
                          }}
                          labelAdditionalStyles={{ left: '18%' }}
                          labelFocusedAdditionalStyles={{ color: 'white' }}
                          onChange={this.handleChange}
                          isRequired
                        >
                          <div className="input-group-prepend">
                            <div className="input-group-text">
                              <FontAwesome name="lock" className="fa-fw" />
                            </div>
                          </div>
                        </FloatingLabel>
                      </Col>
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <small>
                    <Link className={s.brand} to="/forgotPassword">
                      Forgot your password? Click here to restore your password.
                    </Link>
                  </small>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col sm={12}>
                      <Col sm={12}>
                        <LoadingButton
                          isLoading={this.state.isLoading}
                          buttonText="Login"
                          buttonLoadingText="Login"
                          type="submit"
                          className="btn-primary-dark"
                          block
                        />
                      </Col>
                    </Col>
                  </Row>
                </FormGroup>
              </Form>
            </Col>
          </Row>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {
  loginUser,
};

export default connect(
  null,
  mapDispatchToProps,
)(withStyles(FAStyles, s)(Login));
